import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, FileText, User2, Zap, Users, Info, Contact } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";

export const CreditBadge = ({ onUpgrade }) => {
  const { user, credits } = useAuth();
  if (!user || !credits) return null;
  let label = credits.lifetime ? "Pro · unlimited" : `${credits.coins ?? 0} coins`;
  if (!credits.lifetime && credits.topup_coins > 0) label += ` · +${credits.topup_coins}`;
  return (
    <button
      data-testid="search-credit-badge"
      onClick={onUpgrade}
      className="text-xs sm:text-sm px-3 py-1.5 rounded-full border border-[#A6844A]/50 bg-white/5 hover:bg-white/10 transition-colors inline-flex items-center gap-1.5"
    >
      <Zap className="w-3.5 h-3.5 text-[#C5A059]" /> {label}
    </button>
  );
};

export const Header = ({ onUpgrade }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-40 bg-[#1B3A4B] text-white border-b border-[#A6844A]/40"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-0.5" data-testid="brand-link">
          <img src="/logo.png" alt="BidMark Quotes logo" className="h-12 w-12 rounded-xl object-contain" />
          <span className="hidden sm:inline font-serif text-2xl font-bold tracking-tight">BidMark Quotes</span>
        </Link>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="/about" data-testid="nav-about-link" className={`${user ? "hidden sm:inline" : ""} text-sm px-2 sm:px-3 py-1.5 rounded-full hover:bg-white/10 transition-colors`}>About</Link>
          {!user && (
            <Link to="/#pricing" data-testid="nav-pricing-link" className="hidden sm:inline text-sm px-3 py-1.5 rounded-full hover:bg-white/10 transition-colors">Pricing</Link>
          )}
          <CreditBadge onUpgrade={onUpgrade} />
          {user && (
            <Button
              data-testid="nav-leads-button"
              onClick={() => navigate("/leads")}
              className="rounded-full bg-[#A6844A] hover:bg-[#8f6f3b] text-white h-9 px-4"
            >
              <Users className="w-4 h-4 mr-2" />
              Leads
            </Button>
          )}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="account-menu-trigger"
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-3 py-1.5 text-sm hover:bg-white/10 transition-colors"
                >
                  <User2 className="w-4 h-4" />
                  <span>Menu</span>
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem data-testid="menu-saved-quotes" onClick={() => navigate("/quotes")}>
                  <FileText className="w-4 h-4 mr-2" /> Saved quotes
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-saved-customers" onClick={() => navigate("/customers")}>
                  <Contact className="w-4 h-4 mr-2" /> Saved customers
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-account" onClick={() => navigate("/account")}>
                  <User2 className="w-4 h-4 mr-2" /> Account
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-upgrade" onClick={onUpgrade}>
                  <Zap className="w-4 h-4 mr-2" /> Plan & searches
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-about" onClick={() => navigate("/about")}>
                  <Info className="w-4 h-4 mr-2" /> About
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-button" onClick={doLogout}>
                  <LogOut className="w-4 h-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button
                data-testid="header-login-button"
                variant="ghost"
                onClick={() => navigate("/login")}
                className="text-white hover:bg-white/10 hover:text-white rounded-full"
              >
                Log in
              </Button>
              <Button
                data-testid="header-signup-button"
                onClick={() => navigate("/register")}
                className="rounded-full bg-[#A6844A] hover:bg-[#8f6f3b] text-white"
              >
                Sign up
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};
